import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Popover,
  ActionIcon,
  Indicator,
  Stack,
  Group,
  Text,
  Avatar,
  ScrollArea,
  Button,
  Divider,
  Center,
  Loader,
} from '@mantine/core';
import { IconBell, IconUser, IconTrophy, IconThumbUp } from '@tabler/icons-react';
import { notificationsService, type Notification } from '../../services/notifications';

function formatRelative(dateString: string) {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (hours < 24) return `${hours}h`;
  return `${days}d`;
}

function NotificationItem({ n, onRead }: { n: Notification; onRead: (id: string) => void }) {
  const href = n.reviewId ? `/reviews/${n.reviewId}` : '/notifications';

  return (
    <Group
      renderRoot={(props) => <Link to={href} {...props} />}
      onClick={() => !n.isRead && onRead(n.id)}
      gap="sm"
      wrap="nowrap"
      style={{
        padding: '0.5rem 0.75rem',
        textDecoration: 'none',
        color: 'inherit',
        backgroundColor: n.isRead ? 'transparent' : 'var(--mantine-color-brand-light)',
        borderRadius: 'var(--mantine-radius-sm)',
        transition: 'background-color 150ms',
      }}
    >
      <Avatar
        src={n.typeName === 'BadgeEarned' || n.typeName === 'HelpfulVote' ? undefined : n.actor?.profileImageUrl}
        size={32}
        radius="xl"
        color={n.typeName === 'BadgeEarned' ? 'yellow' : n.typeName === 'HelpfulVote' ? 'teal' : undefined}
      >
        {n.typeName === 'BadgeEarned' ? <IconTrophy size={14} /> : n.typeName === 'HelpfulVote' ? <IconThumbUp size={14} /> : <IconUser size={14} />}
      </Avatar>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text size="xs" lineClamp={2}>{n.message ?? n.typeName}</Text>
        <Text size="xs" c="dimmed">{formatRelative(n.createdAt)}</Text>
      </Stack>
      {!n.isRead && (
        <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'var(--mantine-color-brand-6)', flexShrink: 0 }} />
      )}
    </Group>
  );
}

export function NotificationBell({ iconVariant = 'subtle', iconColor, iconSize = 'lg' }: {
  iconVariant?: 'subtle' | 'filled' | 'light';
  iconColor?: string;
  iconSize?: string | number;
} = {}) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-preview'],
    queryFn: () => notificationsService.getNotifications(1, 5),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsService.markAsRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications-preview'] }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications-preview'] }),
  });

  return (
    <Popover width={320} position="bottom-end" shadow="md" radius="md" withinPortal>
      <Popover.Target>
        <Indicator
          label={unreadCount > 0 ? String(unreadCount > 9 ? '9+' : unreadCount) : undefined}
          disabled={unreadCount === 0}
          size={16}
          color="red"
          offset={4}
        >
          <ActionIcon size={iconSize} radius="xl" variant={iconVariant} color={iconColor} aria-label="Notifications">
            <IconBell size={18} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        {/* Header */}
        <Group justify="space-between" px="sm" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={600} size="sm">Notifications</Text>
          {unreadCount > 0 && (
            <Button
              size="compact-xs"
              variant="subtle"
              loading={markAllMutation.isPending}
              onClick={() => markAllMutation.mutate()}
            >
              Mark all read
            </Button>
          )}
        </Group>

        {/* List */}
        <ScrollArea.Autosize mah={360}>
          {isLoading ? (
            <Center py="lg"><Loader size="sm" /></Center>
          ) : notifications.length === 0 ? (
            <Center py="xl">
              <Text size="sm" c="dimmed">No notifications yet</Text>
            </Center>
          ) : (
            <Stack gap={2} p="xs">
              {notifications.map((n) => (
                <NotificationItem key={n.id} n={n} onRead={(id) => markReadMutation.mutate(id)} />
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>

        {/* Footer */}
        <Divider />
        <Center py="xs">
          <Button component={Link} to="/notifications" variant="subtle" size="xs">
            View all notifications
          </Button>
        </Center>
      </Popover.Dropdown>
    </Popover>
  );
}
